import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

/**
 * La session d'authentification du parcours tiers, dans la coque native.
 *
 * Dans un navigateur, se connecter avec Google est une navigation : on quitte la page, on
 * revient. Dans une application, il n'y a nulle part où aller — la WebView **est**
 * l'application, et l'y envoyer la ferait disparaître derrière un écran de consentement.
 * Le système ouvre donc un navigateur par-dessus, et rend l'adresse de retour à
 * l'application par un événement d'ouverture d'URL.
 *
 * Rien de ce qui prouve la légitimité du retour ne passe par le téléphone : l'état, le
 * nonce et le vérificateur PKCE naissent et meurent côté serveur. Le téléphone ne manipule
 * qu'un code de reprise à usage unique, et l'échange des jetons se fait hors de toute URL.
 *
 * La fermeture volontaire est traitée comme telle — `null`, pas une erreur. Un parent qui
 * ferme l'écran de consentement a changé d'avis ; lui répondre « la connexion a échoué »
 * lui ferait croire à une panne.
 */

/**
 * Adresse de retour du parcours.
 *
 * Miroir exact de ce que l'API fabrique (`OidcService.urlRetour`, `APP_MOBILE_SCHEME`) :
 * les deux valeurs doivent bouger ensemble. Le schéma `repere://` est aussi déclaré dans
 * la configuration des fournisseurs d'identité — c'est pourquoi il n'a pas changé avec la
 * coque.
 */
export const RETOUR_NATIF = 'repere://connexion';

/**
 * Ouvre le parcours et rend les paramètres de l'adresse de retour.
 *
 * `null` quand le parent a refermé le navigateur sans aboutir.
 */
export async function sessionAuthentification(
  urlAutorisation: string,
): Promise<URLSearchParams | null> {
  let resoudre: (issue: URLSearchParams | null) => void = () => undefined;
  const attente = new Promise<URLSearchParams | null>((tenir) => {
    resoudre = tenir;
  });

  // Les deux écouteurs sont posés avant l'ouverture : le retour peut arriver avant que
  // `Browser.open` ait rendu la main, et un écouteur posé après l'aurait manqué.
  const ecouteurs = await Promise.all([
    App.addListener('appUrlOpen', ({ url }) => {
      if (!url.startsWith(RETOUR_NATIF)) return;
      // Le navigateur se referme depuis le code : sur Android, il reste sinon posé
      // par-dessus l'application, sur une page morte.
      void Browser.close();
      resoudre(new URL(url).searchParams);
    }),
    Browser.addListener('browserFinished', () => resoudre(null)),
  ]);

  try {
    await Browser.open({ url: urlAutorisation });
    return await attente;
  } finally {
    for (const ecouteur of ecouteurs) await ecouteur.remove();
  }
}
